/**
 * Customer-portal feeding plan editor for one pet (migration 0101).
 *
 * Owners fill in "Morning: 1 cup Royal Canin + supplement" once; every stay
 * then prints it on the run card and staff tick each meal off. Talks to
 * GET/PUT /api/customer/pets/:id/feeding-plan (ownership-checked server
 * side).
 */
import { FeedingPlanEditor } from '@/components/FeedingPlanEditor';
import { FeedingPlanSummary } from '@/components/FeedingPlanSummary';
import {
  type FeedingPlanDraft,
  type FeedingPlanView,
  type PetFeedingPlanResponse,
  draftFromPlan,
  draftToBody,
  validateFeedingDraft,
} from '@/lib/feeding';
import { Loader2, Pencil, Utensils } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { CustomerApiError, customerApi } from '../customer_api';

interface CustomerFeedingPlanPanelProps {
  petId: string;
}

export function CustomerFeedingPlanPanel({ petId }: CustomerFeedingPlanPanelProps) {
  const [plan, setPlan] = useState<FeedingPlanView | null>(null);
  const [legacyText, setLegacyText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FeedingPlanDraft>(draftFromPlan(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customerApi<PetFeedingPlanResponse>(
        `/api/customer/pets/${petId}/feeding-plan`,
      );
      setPlan(res.plan);
      setLegacyText(res.legacyText);
    } catch {
      setError('Could not load the feeding plan.');
    } finally {
      setLoading(false);
    }
  }, [petId]);

  useEffect(() => {
    void load();
  }, [load]);

  function startEdit() {
    setDraft(draftFromPlan(plan));
    setEditing(true);
    setError(null);
  }

  async function save() {
    const issues = validateFeedingDraft(draft);
    if (issues.length > 0) {
      setError(issues.join('. '));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await customerApi<{ plan: FeedingPlanView }>(
        `/api/customer/pets/${petId}/feeding-plan`,
        { method: 'PUT', body: JSON.stringify(draftToBody(draft)) },
      );
      setPlan(res.plan);
      setEditing(false);
    } catch (e) {
      const body = e instanceof CustomerApiError ? (e.body as { issues?: string[] } | null) : null;
      setError(body?.issues?.join('. ') ?? 'Could not save the feeding plan.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="mt-6 rounded-xl border border-border bg-surface p-5"
      data-testid="customer-feeding-plan"
    >
      <div className="mb-3 flex items-center gap-2">
        <Utensils className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h2 className="text-sm font-semibold">Feeding plan</h2>
        {!editing && !loading && (
          <button
            type="button"
            onClick={startEdit}
            className="press ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-secondary"
          >
            <Pencil className="size-3" strokeWidth={2} />
            {plan ? 'Edit' : 'Add plan'}
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Tell us what, how much and when. We print it on the kennel card and tick every meal off, so
        you can see how they ate on their report card.
      </p>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : editing ? (
        <div className="space-y-3">
          <FeedingPlanEditor value={draft} onChange={setDraft} idPrefix={`portal-${petId}`} />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="press inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {saving && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
              Save plan
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="press rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <FeedingPlanSummary
            items={plan?.items ?? []}
            ownerSupplied={plan?.ownerSupplied ?? false}
            allergies={plan?.allergies ?? null}
            redFlags={plan?.redFlags ?? null}
            legacyText={plan ? null : legacyText}
            emptyLabel="No feeding plan yet."
          />
          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        </>
      )}
    </section>
  );
}
